import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import { AppState, Timeout } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import {
  LiveStreamAnswerMessage,
  LiveStreamCandidateMessage,
  LiveStreamSubscribeMessage,
  LiveStreamUnsubscribeMessage,
  OutgoingMessageType,
  PeerCandidate,
} from 'messages/websocket.messages';

import { DEFAULT_PRESENTER_VOLUME } from '../../constants/volume';
import ParticipantRtcStats from '../../preciate-stream/participant-rtc-stats';
import { socialAppActions, socialAppSelectors } from '../../state/app';
import { SubscriptionState } from '../../types/webrtc';
import { SubscriptionType } from '../subscription/subscription.types';
import { VideoRoomService } from '../video-room.service';
import { WebsocketService } from '../websocket.service';

import { Subscriber } from './subscriber.types';

export class LivestreamSubscriber extends Loggable implements Subscriber {
  public _peer?: RTCPeerConnection;
  public peerTime?: number;
  public peerConnectionTime?: number;
  public subscriptionState: SubscriptionState = SubscriptionState.NOT_SUBSCRIBED;
  public rtcStats?: ParticipantRtcStats;
  public rtcVideoStatsInterval?: Timeout;
  public retryTimeout?: Timeout;
  public destroyed$ = new ReplaySubject<boolean>(1);
  public videoStream?: MediaStream;
  public audioStream?: MediaStream;
  public audioElement: HTMLAudioElement;
  public subscriberType?: SubscriptionType;
  public videoRoomService: VideoRoomService;
  public subscriberId: string;
  public videoEnabled: boolean;
  public audioEnabled: boolean;

  public constructor(
    videoRoomService: VideoRoomService,
    subscriberId: string,
    audioEnabled: boolean,
    videoEnabled: boolean,
    private websocketService: WebsocketService,
    private store: Store<AppState>,
    private subscriberPeerTime: number,
    private _onMediaStreamChange: (subscriberId: string, audio?: MediaStream, video?: MediaStream) => void,
  ) {
    super();
    this.setDisplayName('LivestreamSubscriber');

    this.subscriberType = SubscriptionType.LIVE_STREAM;
    this.videoRoomService = videoRoomService;
    this.subscriberId = subscriberId;
    this.audioEnabled = audioEnabled;
    this.videoEnabled = videoEnabled;

    this.audioElement = document.createElement('audio');
    this.audioElement.volume = DEFAULT_PRESENTER_VOLUME / 100;
    this.audioElement.oncanplay = () => this.audioElement.play();

    this.store
      .select(socialAppSelectors.getMixVolume)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((volume) => this.setVolume(volume));
  }

  public onMediaStreamChange(): void {
    this._onMediaStreamChange(this.subscriberId, this.audioStream, this.videoStream);
  }

  public sendSubscribeMessage(): void {
    if (!this.peerTime) {
      return;
    }
    const msg: LiveStreamSubscribeMessage = {
      t: OutgoingMessageType.LS_SUB,
      streamId: this.subscriberId,
      pt: this.peerTime,
    };
    this.websocketService.sendMessage(msg);
  }

  public sendUnSubscribeMessage(): void {
    if (!this.peerTime) {
      return;
    }
    const msg: LiveStreamUnsubscribeMessage = {
      t: OutgoingMessageType.LS_UNSUB,
      streamId: this.subscriberId,
      pt: this.peerTime,
    };
    this.websocketService.sendMessage(msg);
  }

  public sendCandidateMessage(candidate: PeerCandidate, pt: number): void {
    if (!this.peerTime) {
      return;
    }
    const msg: LiveStreamCandidateMessage = {
      t: OutgoingMessageType.LS_CANDIDATE,
      d: candidate,
      pt,
      streamId: this.subscriberId,
    };
    this.websocketService.sendMessage(msg);
  }

  public onIceConnectionStateChange(peer: RTCPeerConnection): void {
    const { iceConnectionState } = peer;
    const { peerTime, subscriberId } = this;
    this.info('Livestream Subscribe ICE state change', { subscriberId, iceConnectionState, peerTime });
  }

  public onIceGatheringStateChange(peer: RTCPeerConnection): void {
    const { iceGatheringState } = peer;
    this.info('Livestream Subscribe ICE gathering state change', {
      subscriberId: this.subscriberId,
      iceGatheringState,
      peerTime: Date.now(),
    });
  }

  public onIceCandidateError(e: Event): void {
    this.trace('Candidate error', this.subscriberId, e);
  }

  public async addOffer(offer: RTCSessionDescriptionInit, peerTime: number): Promise<void> {
    const { subscriberId, subscriberType } = this;
    if (!this.peer) {
      this.warn(`Already unsubscribed from ${subscriberType} ${subscriberId}`);
      return;
    }

    if (this.peerTime !== peerTime) {
      this.warn(`Offer peer time mismatch - ${subscriberType}`, {
        subscriberId,
        storedPeerTime: this.peerTime,
        peerTime,
      });
      return;
    }
    await this.peer.setRemoteDescription(offer);
    this.peer.getTransceivers().forEach((t) => (t.direction = 'recvonly'));
    const answer = await this.peer.createAnswer();
    const { sdp, type } = answer;
    if (!type || !sdp) {
      this.error('addOffer: Got unexpected undefined value in answer', answer);
      return;
    }

    await this.peer.setLocalDescription(answer);

    const msg: LiveStreamAnswerMessage = {
      t: OutgoingMessageType.LS_ANSWER,
      answer: { type, sdp },
      pt: peerTime,
      streamId: this.subscriberId,
    };

    this.websocketService.sendMessage(msg);
  }

  public destroyRtcStats(): void {
    const { subscriberId, subscriberType } = this;
    if (this.rtcVideoStatsInterval) {
      clearInterval(this.rtcVideoStatsInterval);
    }
    if (this.rtcStats) {
      this.info(`RTC Stats STOP for ${subscriberType}`, subscriberId);
    }
    this.rtcStats?.destroy();
    this.rtcVideoStatsInterval = undefined;
    this.rtcStats = undefined;
  }

  public destroy(): void {
    const { subscriberId, subscriberType } = this;
    this.info(`Destroying ${subscriberType} ${subscriberId}`);

    this.store.dispatch(socialAppActions.REMOVE_PARTICIPANT({ userId: this.subscriberId }));
    this.destroyRtcStats();

    this.audioElement.srcObject = null;

    this.destroyed$.next(true);
    this.destroyed$.complete();

    this._onMediaStreamChange(this.subscriberId, undefined, undefined);

    this.peer?.close();
    this.peer = undefined;

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    this.retryTimeout = undefined;
  }

  public get peer(): RTCPeerConnection | undefined {
    return this._peer;
  }

  public set peer(newPeer: RTCPeerConnection | undefined) {
    if (newPeer) {
      this.videoRoomService.rtcStatsStreamService.addSubscriptionMonitoring(
        this.subscriberId,
        SubscriptionType.LIVE_STREAM,
        newPeer,
      );
    } else {
      this.videoRoomService.rtcStatsStreamService.removeSubscriptionMonitoring(this.subscriberId);
    }
    this._peer = newPeer;
  }

  /**
   * Set the audio element volume
   */
  private setVolume(volume: number): void {
    this.audioElement.volume = volume / 100;
  }
}
